import React from 'react';
import { observer } from 'mobx-react';

import { diContainer } from '~setup/diContainer';
import { serviceNames } from '~setup/CartService';

import QuantityItem from '~views/ShoppingBagTab/CartSidebarItems/CartItem/CartItemDescription/QuantityItem/QuantityItem';
import { KitDetails } from '~views/ShoppingBagTab/CartSidebarItems/CartItem/CartItemDescription/KitDetails/KitDetails';
import { classes } from '~utils/ClassNames';
import {
    Description,
    ProductLink,
    ProductName,
    ProductPoints,
    ProductSub,
    ProductDescriptionWrapper
} from './CartItemDescriptionStyles';

import { ICartItemConfig } from '~views/ShoppingBagTab/CartSidebarItems/CartItem/ICartItem';
import ICartItemDescription from './ICartItemDescription';
import { useTranslation } from '~sharedComponents/Providers/Translations/TranslationsHook';
import TextLabel from '~sharedComponents/TextLabel/TextLabel';
import { calculatePoints } from '~utils/LoyaltyPoints';
import { ShadeSize } from './ShadeSize/ShadeSize';

const descriptionName = 'cart-item-description';
const descriptionClass = classes(`${descriptionName}`);
const nameClass = classes(`${descriptionName}-name`);
const subClass = classes(`${descriptionName}-sku-description`);
const pointsClass = classes(`${descriptionName}-points`);
const linkClass = classes(`${descriptionName}-link`);
const productDescriptionClass = classes(`${descriptionName}-product`);

const translationList = ['sizeLabel', 'loyaltyRewardPoints'];

const CartItemDescription: React.FC<ICartItemDescription> = observer(
    ({ cartItem, config, quantitySelectorProps }: ICartItemDescription) => {
        const { product, isKitItem, quantity, skuId, kitInfo, hasEngraving, isDonationItem } =
            cartItem;
        const { redirectClick } = diContainer.get(serviceNames.cartSidebarViewController);

        const { name, description, shadeCode, shade, size, url, price } = product;

        const {
            drawerIsReadOnly = true,
            pageIsReadOnly = true,
            displayLoyaltyPoints = false,
            enableKitDetails = false,
            loyaltyPointsMultiplier = 10
        } = config as ICartItemConfig;

        const { loyaltyRewardPoints } = useTranslation(translationList);

        const productUrl = product.isSample ? '' : url;
        const isReadOnly =
            drawerIsReadOnly || pageIsReadOnly || product.isSample || isKitItem || isDonationItem;
        const displayKitDetailedList = isKitItem && enableKitDetails && !hasEngraving;
        const productPointsLabel = `${calculatePoints({
            price,
            multiplier: loyaltyPointsMultiplier
        })} ${loyaltyRewardPoints}`;
        const kitInfoName = kitInfo?.name || '';
        const kitName = displayKitDetailedList ? kitInfoName : name;
        const shouldDisplayShade = !isDonationItem;

        const onClick = () => !!productUrl && redirectClick(productUrl);

        return (
            <Description className={descriptionClass} data-testid="cart-item-description">
                {isKitItem && !hasEngraving ? (
                    <div className={linkClass}>
                        <ProductName
                            className={nameClass}
                            data-testid="cart-item-description-link-product-name"
                        >
                            <TextLabel text={kitName} />
                        </ProductName>
                    </div>
                ) : (
                    <ProductLink
                        onClick={onClick}
                        className={linkClass}
                        tabIndex={0}
                        data-testid="cart-item-description-link"
                    >
                        <ProductName
                            className={nameClass}
                            data-testid="cart-item-description-link-product-name"
                        >
                            <TextLabel text={name} />
                        </ProductName>
                        <ProductSub
                            className={subClass}
                            data-testid="cart-item-description-link-product-sub"
                        >
                            {description}
                        </ProductSub>
                    </ProductLink>
                )}
                {displayKitDetailedList && <KitDetails cartItem={cartItem} />}
                <ProductDescriptionWrapper
                    aria-readonly={isReadOnly}
                    className={productDescriptionClass}
                >
                    {shouldDisplayShade && (
                        <ShadeSize
                            readonly={isReadOnly}
                            shade={shade}
                            shadeCode={shadeCode}
                            size={size}
                        />
                    )}
                    {displayLoyaltyPoints && (
                        <ProductPoints className={pointsClass}>{productPointsLabel}</ProductPoints>
                    )}
                    <QuantityItem
                        {...quantitySelectorProps}
                        readOnly={isReadOnly}
                        skuId={skuId}
                        quantity={quantity}
                    />
                </ProductDescriptionWrapper>
            </Description>
        );
    }
);
export default CartItemDescription;
